import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Cloud, CloudRain, Sun, Wind, Droplets, Thermometer,
  AlertTriangle, Bell, Settings, MapPin, Clock, Save,
  CloudSnow, CloudLightning, Sunrise, Sunset, Eye, Gauge, Leaf,
  ChevronDown, ChevronUp, CloudDrizzle, RefreshCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';

const MeteoPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [agriAdvice, setAgriAdvice] = useState([]);
  const [expandedDay, setExpandedDay] = useState(null);
  const [lune, setLune] = useState(null);

  const [config, setConfig] = useState({
    nom_lieu: 'Siege Exploitation',
    latitude: 44.3450842,
    longitude: 5.2833145,
    ville: 'Sainte-Jalle'
  });

  const [alertConfig, setAlertConfig] = useState({
    pluie_active: true, pluie_seuil_mm: 5, pluie_delai_heures: 24,
    temperature_basse_active: true, temperature_basse_seuil: 2, temperature_basse_delai: 12,
    temperature_haute_active: false, temperature_haute_seuil: 35, temperature_haute_delai: 24,
    vent_actif: true, vent_seuil_kmh: 50, vent_delai_heures: 12,
    gel_actif: true, gel_delai_heures: 24,
    notification_email: true, notification_sms: false, notification_telegram: true
  });

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => {
    if (config.latitude && config.longitude) fetchWeather();
  }, [config.latitude, config.longitude]);

  const loadConfig = async () => {
    try {
      const res = await api.get('/meteo/config');
      if (res.data.config) {
        setConfig(res.data.config.lieu || config);
        setAlertConfig(prev => ({ ...prev, ...res.data.config.alertes }));
      }
    } catch (err) {
      console.log('Config par defaut');
    }
  };

  const fetchWeather = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/meteo/forecast?lat=${config.latitude}&lon=${config.longitude}`);
      setWeather(res.data.current);
      setForecast(res.data.forecast || []);
      setHourly(res.data.hourly_today || []);
      setAlerts(res.data.alerts || []);
      setAgriAdvice(res.data.conseils_agricoles || []);
      setLune(res.data.lune || null);
    } catch (err) {
      console.error('Erreur meteo:', err);
      toast({ title: 'Erreur', description: 'Impossible de charger la meteo', variant: 'destructive' });
    }
    setLoading(false);
  }, [config.latitude, config.longitude]);

  const saveConfig = async () => {
    try {
      await api.post('/meteo/config', { lieu: config, alertes: alertConfig });
      toast({ title: 'Configuration sauvegardee' });
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    }
  };

  const getWeatherIcon = (icone, size = "w-12 h-12") => {
    const cls = size;
    switch (icone) {
      case 'sun': return <Sun className={`${cls} text-yellow-500`} />;
      case 'partly-cloudy': return <Cloud className={`${cls} text-amber-400`} />;
      case 'rain': return <CloudRain className={`${cls} text-blue-500`} />;
      case 'snow': return <CloudSnow className={`${cls} text-blue-200`} />;
      case 'storm': return <CloudLightning className={`${cls} text-purple-500`} />;
      case 'cloudy': return <Cloud className={`${cls} text-gray-400`} />;
      default: return <Cloud className={`${cls} text-gray-400`} />;
    }
  };

  const getMoonIcon = (phaseId, size = "w-10 h-10") => {
    const moons = {
      new: { symbol: "🌑", label: "Nouvelle Lune" },
      waxing_crescent: { symbol: "🌒", label: "Premier Croissant" },
      first_quarter: { symbol: "🌓", label: "Premier Quartier" },
      waxing_gibbous: { symbol: "🌔", label: "Gibbeuse Croissante" },
      full: { symbol: "🌕", label: "Pleine Lune" },
      waning_gibbous: { symbol: "🌖", label: "Gibbeuse Decroissante" },
      last_quarter: { symbol: "🌗", label: "Dernier Quartier" },
      waning_crescent: { symbol: "🌘", label: "Dernier Croissant" },
    };
    const m = moons[phaseId] || moons.new;
    return <span className={size} role="img" aria-label={m.label}>{m.symbol}</span>;
  };

  const getRainBadge = (prob, mm) => {
    if (mm > 0) {
      return (
        <Badge className="bg-blue-600 text-white text-xs">
          <Droplets className="w-3 h-3 mr-1" />{mm}mm
        </Badge>
      );
    }
    if (prob > 30) {
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
          <CloudDrizzle className="w-3 h-3 mr-1" />{prob}%
        </Badge>
      );
    }
    if (prob > 0) {
      return (
        <span className="text-xs text-muted-foreground">{prob}%</span>
      );
    }
    return <span className="text-xs text-green-600">0%</span>;
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl mx-auto" data-testid="meteo-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <Cloud className="w-7 h-7 text-blue-500" />
            Meteo Agricole
          </h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4" />
            {config.ville} ({config.latitude.toFixed(2)}, {config.longitude.toFixed(2)})
          </p>
        </div>
        <Button onClick={fetchWeather} variant="outline" size="sm" disabled={loading} data-testid="refresh-meteo">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Alertes */}
      {alerts.length > 0 && (
        <Card className="border-orange-400 bg-orange-50/80" data-testid="meteo-alerts">
          <CardContent className="py-3">
            <div className="space-y-2">
              {alerts.map((alert, idx) => (
                <div key={idx} className={`flex items-center justify-between p-2.5 rounded-lg ${
                  alert.niveau === 'danger' ? 'bg-red-100 text-red-800' :
                  alert.niveau === 'info' ? 'bg-blue-100 text-blue-800' :
                  'bg-orange-100 text-orange-800'
                }`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">{alert.message}</span>
                  </div>
                  <Badge variant={alert.niveau === 'danger' ? 'destructive' : 'secondary'} className="text-xs">
                    {alert.timing}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="actuel" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="actuel">Actuel</TabsTrigger>
          <TabsTrigger value="previsions" data-testid="tab-previsions">10 jours</TabsTrigger>
          <TabsTrigger value="lune" data-testid="tab-lune">Lune</TabsTrigger>
          <TabsTrigger value="alertes"><Bell className="w-4 h-4 mr-1" />Alertes</TabsTrigger>
          <TabsTrigger value="lieu"><Settings className="w-4 h-4 mr-1" />Lieu</TabsTrigger>
        </TabsList>

        {/* ===== METEO ACTUELLE ===== */}
        <TabsContent value="actuel" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="md:col-span-2">
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : weather && (
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      {getWeatherIcon(weather.icone)}
                      <p className="text-base mt-2">{weather.description}</p>
                    </div>
                    <div className="flex-1">
                      <div className="text-5xl font-bold">{weather.temperature}°C</div>
                      <p className="text-muted-foreground text-sm">Ressenti {weather.ressenti}°C</p>
                      {weather.precipitation_actuelle > 0 && (
                        <p className="text-blue-600 text-sm mt-1 font-medium">
                          Precipitation en cours : {weather.precipitation_actuelle}mm
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 text-sm">
                {[
                  { icon: Droplets, label: 'Humidite', value: `${weather?.humidite || 0}%` },
                  { icon: Wind, label: 'Vent', value: `${weather?.vent_kmh || 0} km/h ${weather?.direction_vent || ''}` },
                  { icon: Gauge, label: 'Pression', value: `${weather?.pression || 0} hPa` },
                  { icon: Eye, label: 'Point rosee', value: weather?.point_rosee != null ? `${weather.point_rosee}°C` : '-' },
                  { icon: Sun, label: 'UV', value: `${weather?.uv || 0}` },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <item.icon className="w-4 h-4" /> {item.label}
                    </span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Heures du jour */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-5 h-5" /> Aujourd'hui - heure par heure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {hourly.length > 0 ? hourly.map((h, idx) => (
                  <div key={idx} className="flex flex-col items-center min-w-[70px] p-2 rounded-lg bg-muted/40 text-center">
                    <span className="text-xs font-medium text-muted-foreground">{h.heure}</span>
                    {getWeatherIcon(h.icone, "w-5 h-5")}
                    <span className="font-semibold text-sm">{h.temperature}°</span>
                    <div className="text-[10px] mt-0.5">
                      {h.precipitation_mm > 0 ? (
                        <span className="text-blue-600 font-medium">{h.precipitation_mm}mm</span>
                      ) : h.precipitation_prob > 0 ? (
                        <span className="text-blue-400">{h.precipitation_prob}%</span>
                      ) : (
                        <span className="text-green-500">0%</span>
                      )}
                    </div>
                    {h.temp_sol != null && (
                      <span className="text-[10px] text-amber-600">Sol {h.temp_sol}°</span>
                    )}
                  </div>
                )) : (
                  <p className="text-muted-foreground text-sm">Chargement...</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Conseils agricoles */}
          {agriAdvice.length > 0 && (
            <Card className="border-green-200 bg-green-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-green-800 text-base">
                  <Leaf className="w-5 h-5" /> Conseils Agricoles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="agri-advice">
                {agriAdvice.map((a, idx) => {
                  const colors = {
                    haute: 'bg-red-100 text-red-800 border-red-200',
                    moyenne: 'bg-amber-100 text-amber-800 border-amber-200',
                    info: 'bg-blue-100 text-blue-800 border-blue-200'
                  };
                  return (
                    <div key={idx} className={`p-2.5 rounded-lg border ${colors[a.priority] || colors.info}`}>
                      <p className="font-medium text-sm">{a.message}</p>
                      <p className="text-xs mt-0.5 opacity-80">{a.detail}</p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== PREVISIONS 10 JOURS ===== */}
        <TabsContent value="previsions" className="space-y-3" data-testid="previsions-10j">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Previsions 10 jours</h2>
            <p className="text-xs text-muted-foreground">Cliquez sur un jour pour le detail horaire</p>
          </div>

          {forecast.map((jour, idx) => (
            <Card key={idx} className="overflow-hidden" data-testid={`forecast-day-${idx}`}>
              {/* Ligne resume du jour */}
              <div
                className="flex items-center justify-between p-3 md:p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedDay(expandedDay === idx ? null : idx)}
              >
                <div className="flex items-center gap-3 min-w-[140px]">
                  {getWeatherIcon(jour.icone, "w-7 h-7")}
                  <div>
                    <span className="font-semibold text-sm block">{jour.jour}</span>
                    <span className="text-xs text-muted-foreground">{jour.date}</span>
                  </div>
                </div>

                <span className="text-xs text-muted-foreground hidden md:block flex-1 ml-4">{jour.description}</span>

                <div className="flex items-center gap-3 md:gap-5">
                  {/* Pluie */}
                  <div className="flex flex-col items-center min-w-[50px]" data-testid={`rain-day-${idx}`}>
                    {getRainBadge(jour.pluie_prob, jour.pluie_mm)}
                    {jour.heures_pluie > 0 && (
                      <span className="text-[10px] text-muted-foreground">{jour.heures_pluie}h</span>
                    )}
                  </div>

                  {/* Vent */}
                  <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground min-w-[45px]">
                    <Wind className="w-3 h-3" /> {jour.vent_max_kmh}
                  </div>

                  {/* ETP */}
                  {jour.evapotranspiration > 0 && (
                    <span className="hidden lg:block text-xs text-amber-600 min-w-[55px]" title="Evapotranspiration">
                      ETP {jour.evapotranspiration}mm
                    </span>
                  )}

                  {/* Lever/coucher */}
                  <div className="hidden lg:flex flex-col text-[10px] text-muted-foreground min-w-[50px]">
                    {jour.lever_soleil && <span><Sunrise className="w-3 h-3 inline text-orange-400" /> {jour.lever_soleil}</span>}
                    {jour.coucher_soleil && <span><Sunset className="w-3 h-3 inline text-orange-600" /> {jour.coucher_soleil}</span>}
                  </div>

                  {/* Temperatures */}
                  <div className="font-semibold text-sm min-w-[65px] text-right">
                    <span className="text-blue-600">{jour.temp_min}°</span>
                    <span className="text-muted-foreground mx-0.5">/</span>
                    <span className="text-red-500">{jour.temp_max}°</span>
                  </div>

                  {expandedDay === idx
                    ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  }
                </div>
              </div>

              {/* Detail horaire (expanded) */}
              {expandedDay === idx && jour.heures && (
                <div className="border-t bg-muted/10 px-3 py-3">
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-1.5">
                    {jour.heures.map((h, hIdx) => (
                      <div key={hIdx} className="flex flex-col items-center p-1.5 rounded-md bg-background border text-center">
                        <span className="text-[11px] font-medium text-muted-foreground">{h.heure}</span>
                        {getWeatherIcon(h.icone, "w-5 h-5")}
                        <span className="font-semibold text-sm">{h.temperature}°</span>
                        <div className="text-[10px] mt-0.5">
                          {h.precipitation_mm > 0 ? (
                            <span className="text-blue-600 font-bold">{h.precipitation_mm}mm</span>
                          ) : (
                            <span className={h.precipitation_prob > 20 ? 'text-blue-500' : 'text-muted-foreground'}>
                              {h.precipitation_prob}%
                            </span>
                          )}
                        </div>
                        <div className="text-[9px] text-muted-foreground mt-0.5 space-y-px">
                          <div><Wind className="w-2.5 h-2.5 inline" /> {h.vent_kmh}{h.rafales_kmh > h.vent_kmh + 10 ? <span className="text-orange-500"> (r{h.rafales_kmh})</span> : ''}</div>
                          <div><Droplets className="w-2.5 h-2.5 inline" /> {h.humidite}%</div>
                          {h.temp_sol != null && <div className="text-amber-600">Sol {h.temp_sol}°</div>}
                          {h.couverture_nuageuse != null && <div><Cloud className="w-2.5 h-2.5 inline" /> {h.couverture_nuageuse}%</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Lien voir toutes les heures */}
                  {jour.heures_completes && jour.heures_completes.length > jour.heures.length && (
                    <HourlyFullDetail heures={jour.heures_completes} getWeatherIcon={getWeatherIcon} />
                  )}
                </div>
              )}
            </Card>
          ))}
        </TabsContent>


        {/* ===== LUNE ===== */}
        <TabsContent value="lune" className="space-y-4" data-testid="lune-section">
          {lune ? (
            <>
              {/* Lune actuelle */}
              <Card className="overflow-hidden">
                <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-violet-950 text-white p-6">
                  <div className="flex items-center gap-6">
                    <div className="text-6xl">{getMoonIcon(lune.phase_id, "text-6xl")}</div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold">{lune.phase}</h2>
                      <p className="text-indigo-200 text-sm mt-1">
                        Illumination : {lune.illumination}% | Age : {lune.age_jours} jours
                      </p>
                      <div className="flex gap-3 mt-3">
                        <Badge className="bg-indigo-700/50 text-indigo-100 border border-indigo-500/30">
                          {lune.direction}
                        </Badge>
                        <Badge className="bg-emerald-700/50 text-emerald-100 border border-emerald-500/30 capitalize">
                          Jour {lune.type_jour}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Barre d'illumination */}
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-indigo-300 mb-1">
                      <span>Nouvelle Lune</span>
                      <span>Pleine Lune</span>
                    </div>
                    <div className="w-full bg-indigo-900/50 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-indigo-400 to-yellow-200 h-2 rounded-full transition-all"
                        style={{ width: `${lune.illumination}%` }}
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Conseils lunaires */}
              {lune.conseils && lune.conseils.length > 0 && (
                <Card className="border-indigo-200 bg-indigo-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-indigo-900 flex items-center gap-2">
                      <Leaf className="w-5 h-5" />Conseils Lunaires Agricoles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {lune.conseils.map((c, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-white border border-indigo-100">
                        <p className="font-medium text-sm text-indigo-900">{c.conseil}</p>
                        {c.detail && (
                          <p className="text-xs text-indigo-600 mt-1">{c.detail}</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Prochaines phases */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Prochaines phases</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(lune.prochaines_phases || []).map((p, idx) => (
                      <div key={idx} className="flex flex-col items-center p-3 rounded-lg bg-slate-50 border text-center">
                        <span className="text-2xl">{getMoonIcon(p.phase_id, "text-2xl")}</span>
                        <span className="font-medium text-sm mt-1">{p.phase}</span>
                        <span className="text-xs text-muted-foreground">{p.date}</span>
                        <span className="text-xs text-indigo-600 mt-0.5">dans {p.dans_jours}j</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Phases 10 jours */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Calendrier lunaire 10 jours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {(lune.phases_10j || []).map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30 transition-colors">
                        <div className="flex items-center gap-3 min-w-[130px]">
                          <span className="text-xl">{getMoonIcon(p.phase_id, "text-xl")}</span>
                          <div>
                            <span className="font-medium text-sm">{p.jour}</span>
                            <span className="text-xs text-muted-foreground ml-2">{p.date}</span>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground hidden md:block">{p.phase}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize text-xs">{p.type_jour}</Badge>
                          <span className="text-xs w-10 text-right">{p.illumination}%</span>
                          <Badge className={`text-[10px] ${p.montante ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                            {p.montante ? 'Montante' : 'Descendante'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                Chargement des donnees lunaires...
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== ALERTES CONFIG ===== */}
        <TabsContent value="alertes" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" />Alertes Pluie</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Activer les alertes pluie</Label>
                <Switch checked={alertConfig.pluie_active} onCheckedChange={(v) => setAlertConfig(c => ({...c, pluie_active: v}))} />
              </div>
              {alertConfig.pluie_active && (
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Seuil (mm)</Label><Input type="number" value={alertConfig.pluie_seuil_mm} onChange={(e) => setAlertConfig(c => ({...c, pluie_seuil_mm: parseInt(e.target.value)}))} /><p className="text-xs text-muted-foreground mt-1">Alerte si pluie >= ce seuil</p></div>
                  <div><Label>Delai d'alerte (heures)</Label><Input type="number" value={alertConfig.pluie_delai_heures} onChange={(e) => setAlertConfig(c => ({...c, pluie_delai_heures: parseInt(e.target.value)}))} /></div>
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Thermometer className="w-5 h-5" />Alertes Temperature</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-blue-50 space-y-3">
                <div className="flex items-center justify-between"><Label className="text-blue-800">Alerte temperature basse</Label><Switch checked={alertConfig.temperature_basse_active} onCheckedChange={(v) => setAlertConfig(c => ({...c, temperature_basse_active: v}))} /></div>
                {alertConfig.temperature_basse_active && (<div className="grid grid-cols-2 gap-4"><div><Label>Seuil (°C)</Label><Input type="number" value={alertConfig.temperature_basse_seuil} onChange={(e) => setAlertConfig(c => ({...c, temperature_basse_seuil: parseInt(e.target.value)}))} /></div><div><Label>Delai (heures)</Label><Input type="number" value={alertConfig.temperature_basse_delai} onChange={(e) => setAlertConfig(c => ({...c, temperature_basse_delai: parseInt(e.target.value)}))} /></div></div>)}
              </div>
              <div className="p-3 rounded-lg bg-red-50 space-y-3">
                <div className="flex items-center justify-between"><Label className="text-red-800">Alerte temperature haute</Label><Switch checked={alertConfig.temperature_haute_active} onCheckedChange={(v) => setAlertConfig(c => ({...c, temperature_haute_active: v}))} /></div>
                {alertConfig.temperature_haute_active && (<div className="grid grid-cols-2 gap-4"><div><Label>Seuil (°C)</Label><Input type="number" value={alertConfig.temperature_haute_seuil} onChange={(e) => setAlertConfig(c => ({...c, temperature_haute_seuil: parseInt(e.target.value)}))} /></div><div><Label>Delai (heures)</Label><Input type="number" value={alertConfig.temperature_haute_delai} onChange={(e) => setAlertConfig(c => ({...c, temperature_haute_delai: parseInt(e.target.value)}))} /></div></div>)}
              </div>
              <div className="p-3 rounded-lg bg-cyan-50 space-y-3">
                <div className="flex items-center justify-between"><Label className="text-cyan-800">Alerte Gel (inferieur ou egal 0°C)</Label><Switch checked={alertConfig.gel_actif} onCheckedChange={(v) => setAlertConfig(c => ({...c, gel_actif: v}))} /></div>
                {alertConfig.gel_actif && (<div><Label>Delai d'alerte (heures)</Label><Input type="number" value={alertConfig.gel_delai_heures} onChange={(e) => setAlertConfig(c => ({...c, gel_delai_heures: parseInt(e.target.value)}))} className="max-w-[200px]" /></div>)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Wind className="w-5 h-5" />Alertes Vent</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between"><Label>Activer les alertes vent</Label><Switch checked={alertConfig.vent_actif} onCheckedChange={(v) => setAlertConfig(c => ({...c, vent_actif: v}))} /></div>
              {alertConfig.vent_actif && (<div className="grid grid-cols-2 gap-4"><div><Label>Seuil (km/h)</Label><Input type="number" value={alertConfig.vent_seuil_kmh} onChange={(e) => setAlertConfig(c => ({...c, vent_seuil_kmh: parseInt(e.target.value)}))} /></div><div><Label>Delai (heures)</Label><Input type="number" value={alertConfig.vent_delai_heures} onChange={(e) => setAlertConfig(c => ({...c, vent_delai_heures: parseInt(e.target.value)}))} /></div></div>)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Mode de notification</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between"><Label>Email</Label><Switch checked={alertConfig.notification_email} onCheckedChange={(v) => setAlertConfig(c => ({...c, notification_email: v}))} /></div>
              <div className="flex items-center justify-between"><Label>SMS</Label><Switch checked={alertConfig.notification_sms} onCheckedChange={(v) => setAlertConfig(c => ({...c, notification_sms: v}))} /></div>
              <div className="flex items-center justify-between"><Label>Telegram</Label><Switch checked={alertConfig.notification_telegram} onCheckedChange={(v) => setAlertConfig(c => ({...c, notification_telegram: v}))} /></div>
            </CardContent>
          </Card>
          <Button onClick={saveConfig} className="w-full"><Save className="w-4 h-4 mr-2" />Sauvegarder les alertes</Button>
        </TabsContent>

        {/* ===== CONFIG LIEU ===== */}
        <TabsContent value="lieu">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" />Siege de l'exploitation</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Nom du lieu</Label><Input value={config.nom_lieu} onChange={(e) => setConfig(c => ({...c, nom_lieu: e.target.value}))} /></div>
              <div><Label>Ville</Label><Input value={config.ville} onChange={(e) => setConfig(c => ({...c, ville: e.target.value}))} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Latitude</Label><Input type="number" step="0.001" value={config.latitude} onChange={(e) => setConfig(c => ({...c, latitude: parseFloat(e.target.value)}))} /></div>
                <div><Label>Longitude</Label><Input type="number" step="0.001" value={config.longitude} onChange={(e) => setConfig(c => ({...c, longitude: parseFloat(e.target.value)}))} /></div>
              </div>
              <p className="text-sm text-muted-foreground">Trouvez vos coordonnees sur Google Maps (clic droit sur votre exploitation)</p>
              <Button onClick={saveConfig} className="w-full"><Save className="w-4 h-4 mr-2" />Enregistrer le lieu</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

/** Detail horaire complet (24h) - affiche en toggle */
const HourlyFullDetail = ({ heures, getWeatherIcon }) => {
  const [show, setShow] = useState(false);
  if (!heures || heures.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setShow(!show)}
        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
      >
        {show ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {show ? 'Masquer' : 'Voir les 24 heures completes'}
      </button>
      {show && (
        <div className="grid grid-cols-6 md:grid-cols-12 gap-1 mt-2">
          {heures.map((h, i) => (
            <div key={i} className="flex flex-col items-center p-1 rounded bg-background border text-center">
              <span className="text-[10px] font-medium">{h.heure}</span>
              {getWeatherIcon(h.icone, "w-4 h-4")}
              <span className="text-xs font-semibold">{h.temperature}°</span>
              <span className={`text-[9px] ${h.precipitation_mm > 0 ? 'text-blue-600 font-bold' : h.precipitation_prob > 20 ? 'text-blue-400' : 'text-muted-foreground'}`}>
                {h.precipitation_mm > 0 ? `${h.precipitation_mm}mm` : `${h.precipitation_prob}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MeteoPage;
